import { useCallback, useId, useMemo } from 'react';
import debounce from 'lodash.debounce';
import memoizeOne from 'memoize-one';

import { useLanguage } from './../Language/index.js';

const missingTranslations: Record<string, Record<string, string[]>> = {};

export const warnMissingTranslations = debounce(
  memoizeOne((content) => {
    console.warn(`No translation found for:
${content}
  `);
  }),
  5000
);

export const registerMissingTranslation = (id: string, literal: string, locale: string) => {
  missingTranslations[locale] = {
    ...missingTranslations[locale],
    [literal]: missingTranslations[locale]?.[literal]
      ? [...missingTranslations[locale][literal], id].filter(
          (value, index, self) => self.indexOf(value) === index
        )
      : [id],
  };
  warnMissingTranslations.cancel();
  warnMissingTranslations(JSON.stringify(missingTranslations, null, 2));
};

export const useTranslate = (): ((literal: string) => string) => {
  const id = useId();
  const { locale, locales } = useLanguage();

  const translations = useMemo(
    () => ((locales as any)[locale] ? (locales as any)[locale] : {}),
    [locale, locales]
  );

  return useCallback(
    (literal: string) => {
      const translatedText = translations[id ?? literal] ?? translations[literal];
      if (import.meta.env.DEV) {
        // Omit this warning for english locale due it's the default one
        if (!translatedText && locale !== 'en') {
          registerMissingTranslation(id, literal, locale);
        }
      }
      // We should use the literal as fallback
      return translatedText || literal;
    },
    [id, locale, translations]
  );
};

export default useTranslate;
